import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import { AgentBotActuationSection } from '../../components/Atendimento/AgentBotActuationSection';
import { AgentBotClassificationEditor } from '../../components/Atendimento/AgentBotClassificationEditor';
import { AgentBotSignatureSection } from '../../components/Atendimento/AgentBotSignatureSection';
import { agentBotService } from '../../services/agentBotService';
import { inboxService } from '../../services/inboxService';
import { supportGroupService } from '../../services/supportGroupService';
import { workspaceService } from '../../services/workspaceService';
import { AgentBot } from '../../types/agentBot';
import { Inbox } from '../../types/inbox';
import { SupportGroup } from '../../types/supportGroup';
import { AgentBotType, AGENT_BOT_TYPE_LABELS } from '../../types/atendimentoEnums';
import { useToast } from '../../components/UI/ToastProvider';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { helpdeskRoutes } from '../../routes/helpdeskRoutes';

type AgentBotEditTab = 'geral' | 'atuacao' | 'classificacao' | 'assinatura';

interface WorkspaceOption {
  id: string;
  name: string;
}

const parseTab = (value: string | null): AgentBotEditTab => {
  if (value === 'atuacao' || value === 'classificacao' || value === 'assinatura')
  {
    return value;
  }

  return 'geral';
};

export const AgentBotFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bot, setBot] = useState<AgentBot | null>(null);
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [groups, setGroups] = useState<SupportGroup[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
  const [activeTab, setActiveTab] = useState<AgentBotEditTab>(parseTab(searchParams.get('tab')));
  const [form, setForm] = useState({
    name: '',
    description: '',
    botType: AgentBotType.KnowCopilot,
    workspaceId: '',
    outgoingUrl: '',
  });

  useEffect(() => {
    const requested = parseTab(searchParams.get('tab'));
    if (!isEdit && requested !== 'geral')
    {
      setActiveTab('geral');
      return;
    }

    setActiveTab(requested);
  }, [isEdit, searchParams]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try
      {
        const [inboxesData, groupsData, workspacesData] = await Promise.all([
          inboxService.getAll(),
          supportGroupService.getAll(),
          workspaceService.getWorkspaces(),
        ]);
        setInboxes(inboxesData);
        setGroups(groupsData);
        setWorkspaces(workspacesData.map(workspace => ({ id: workspace.id, name: workspace.name })));

        if (isEdit && id)
        {
          const loaded = await agentBotService.getById(id);
          setBot(loaded);
          setForm({
            name: loaded.name,
            description: loaded.description ?? '',
            botType: loaded.botType,
            workspaceId: loaded.workspaceId ?? '',
            outgoingUrl: loaded.outgoingUrl ?? '',
          });
        }
      }
      catch (error)
      {
        addToast(apiErrorMessage(error, 'Erro ao carregar robô'), 'error');
        if (isEdit)
        {
          navigate(helpdeskRoutes.settingsBots, { replace: true });
        }
      }
      finally
      {
        setLoading(false);
      }
    };

    void load();
  }, [id, isEdit]);

  const selectTab = (tab: AgentBotEditTab) => {
    if (!isEdit && tab !== 'geral')
    {
      addToast('Salve o robô para configurar atuação, classificação e assinatura.', 'info');
      return;
    }

    setActiveTab(tab);
    if (tab === 'geral')
    {
      setSearchParams({}, { replace: true });
      return;
    }

    setSearchParams({ tab }, { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim())
    {
      addToast('Nome é obrigatório', 'error');
      return;
    }

    setSaving(true);
    try
    {
      const payload = {
        name: form.name.trim(),
        description: form.description || undefined,
        botType: form.botType,
        workspaceId: form.workspaceId || undefined,
        outgoingUrl: form.outgoingUrl || undefined,
      };

      if (isEdit && id)
      {
        const updated = await agentBotService.update(id, payload);
        setBot(updated);
        addToast('Robô atualizado', 'success');
      }
      else
      {
        const created = await agentBotService.create(payload);
        addToast('Robô criado', 'success');
        navigate(helpdeskRoutes.settingsBotEdit(created.id), { replace: true });
      }
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Erro ao salvar robô'), 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  if (loading)
  {
    return (
      <div className="flex justify-center py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  const tabClass = (tab: AgentBotEditTab, disabled: boolean) => {
    const active = activeTab === tab;
    if (disabled)
    {
      return 'px-6 py-3 font-medium text-gray-400 cursor-not-allowed';
    }

    return `px-6 py-3 font-medium ${
      active
        ? 'border-b-2 border-blue-500 text-blue-600'
        : 'text-gray-500 hover:text-gray-700'
    }`;
  };

  return (
    <div className="max-w-4xl">
      <Link to={helpdeskRoutes.settingsBots} className="text-sm text-blue-600 hover:underline mb-4 inline-block">
        ← Voltar para robôs
      </Link>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {isEdit ? 'Editar robô' : 'Novo robô'}
      </h1>

      <div className="border-b mb-6">
        <nav className="flex gap-1" aria-label="Abas do robô">
          <button
            type="button"
            className={tabClass('geral', false)}
            onClick={() => selectTab('geral')}
          >
            Geral
          </button>
          <button
            type="button"
            className={tabClass('atuacao', !isEdit)}
            onClick={() => selectTab('atuacao')}
            title={!isEdit ? 'Salve o robô para configurar atuação' : undefined}
          >
            Atuação
          </button>
          <button
            type="button"
            className={tabClass('classificacao', !isEdit)}
            onClick={() => selectTab('classificacao')}
            title={!isEdit ? 'Salve o robô para configurar classificação' : undefined}
          >
            Classificação
          </button>
          <button
            type="button"
            className={tabClass('assinatura', !isEdit)}
            onClick={() => selectTab('assinatura')}
            title={!isEdit ? 'Salve o robô para configurar a assinatura' : undefined}
          >
            Assinatura
          </button>
        </nav>
      </div>

      {activeTab === 'geral' && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full border rounded-md px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border rounded-md px-3 py-2 text-sm"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={form.botType}
              onChange={e => setForm(f => ({ ...f, botType: e.target.value as AgentBotType }))}
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              {Object.entries(AGENT_BOT_TYPE_LABELS)
                .filter(([value]) => value !== AgentBotType.Webhook)
                .map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
            </select>
          </div>
          {form.botType === AgentBotType.KnowCopilot && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agente</label>
              <select
                value={form.workspaceId}
                onChange={e => setForm(f => ({ ...f, workspaceId: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm"
              >
                <option value="">Selecione um agente</option>
                {workspaces.map(workspace => (
                  <option key={workspace.id} value={workspace.id}>{workspace.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => navigate(helpdeskRoutes.settingsBots)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      )}

      {isEdit && activeTab === 'atuacao' && bot && (
        <AgentBotActuationSection bot={bot} inboxes={inboxes} groups={groups} />
      )}

      {isEdit && activeTab === 'classificacao' && id && (
        <AgentBotClassificationEditor botId={id} />
      )}

      {isEdit && activeTab === 'assinatura' && id && (
        <AgentBotSignatureSection botId={id} />
      )}
    </div>
  );
};
